// PendingWritesPill — top-nav indicator + retry sheet for the failed-write
// stash (A10, plan U11).
//
// When `getPendingWrites().length > 0`, renders a small `C.warn`-bordered pill
// in the header. Tapping it expands an inline list with retry/dismiss per
// item. Subscribes to `subscribePendingWrites` so the count tracks the stash
// in real time (covers cross-tab updates via the `emit()` in api.ts and a
// fallback `storage` event listener for other browser tabs).
//
// Not a toast — this is a persistent affordance. Per A10 the operator must
// be able to come back hours later and still see "you have 3 unsaved writes".

import { useEffect, useState, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  ApiError,
  ApiNetworkError,
  dismissPendingWrite,
  formatApiError,
  getPendingWritesSnapshot,
  refreshPendingFromStorage,
  retryPendingWrite,
  subscribePendingWrites,
  type PendingWrite,
} from '../lib/api';
import { C, FONT_MONO } from '../lib/design';

const emptySnapshot: PendingWrite[] = [];

function snapshot(): PendingWrite[] {
  // MUST return a referentially-stable value when the underlying state
  // hasn't changed — otherwise useSyncExternalStore re-renders on every
  // tick (React error #185).
  return getPendingWritesSnapshot();
}

function serverSnapshot(): PendingWrite[] {
  return emptySnapshot;
}

function usePendingWrites(): PendingWrite[] {
  // Custom subscribe: bridges both the in-process emitter AND the browser's
  // `storage` event so another tab (or a manual edit in DevTools) reflects
  // here too.
  const subscribe = (cb: () => void) => {
    const unsubLocal = subscribePendingWrites(cb);
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'protocol-pending-writes' || e.key === null) {
        refreshPendingFromStorage();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      unsubLocal();
      window.removeEventListener('storage', onStorage);
    };
  };
  return useSyncExternalStore(subscribe, snapshot, serverSnapshot);
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  const seconds = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function PendingWritesPill() {
  const pending = usePendingWrites();
  const [open, setOpen] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const qc = useQueryClient();

  // Close the sheet automatically when the list empties — there's nothing left
  // to interact with.
  useEffect(() => {
    if (pending.length === 0 && open) setOpen(false);
  }, [pending.length, open]);

  if (pending.length === 0) return null;

  async function onRetry(id: string) {
    setRetryingId(id);
    setLastError(null);
    try {
      await retryPendingWrite(id);
      // Invalidate everything — the operator's most recent write could have
      // affected any query. Cheaper than tracking per-mutation keys here.
      await qc.invalidateQueries();
    } catch (err) {
      if (err instanceof ApiNetworkError) {
        setLastError(
          "Still can't reach the Protocol API. The write was re-stashed — try again when you're back online.",
        );
      } else if (err instanceof ApiError) {
        setLastError(formatApiError(err));
      } else if (err instanceof Error) {
        setLastError(err.message);
      } else {
        setLastError(String(err));
      }
    } finally {
      setRetryingId(null);
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`${pending.length} pending write${pending.length === 1 ? '' : 's'}`}
        style={{
          background: 'transparent',
          color: C.warn,
          border: `1px solid ${C.warn}`,
          borderRadius: 0,
          padding: '6px 10px',
          minHeight: 32,
          fontFamily: FONT_MONO,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        {pending.length} PENDING WRITE{pending.length === 1 ? '' : 'S'}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Pending writes"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            zIndex: 50,
            minWidth: 320,
            maxWidth: 'min(420px, calc(100vw - 32px))',
            background: C.panel,
            border: `1px solid ${C.line}`,
            color: C.text,
            fontFamily: FONT_MONO,
            fontSize: 11,
            letterSpacing: '0.05em',
          }}
        >
          <div
            style={{
              padding: '10px 12px',
              borderBottom: `1px solid ${C.line}`,
              color: C.dim,
              fontSize: 10,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
            }}
          >
            Pending writes ({pending.length})
          </div>

          {lastError && (
            <div
              style={{
                padding: '8px 12px',
                borderBottom: `1px solid ${C.line}`,
                color: C.red,
                fontSize: 10,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
              }}
            >
              {lastError}
            </div>
          )}

          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {pending
              .slice()
              .reverse()
              .map((p) => {
                const isRetrying = retryingId === p.id;
                return (
                  <div
                    key={p.id}
                    style={{
                      padding: '10px 12px',
                      borderBottom: `1px solid ${C.line}`,
                    }}
                  >
                    <div style={{ color: C.text, fontSize: 11 }}>
                      <span style={{ color: C.signal, fontWeight: 700 }}>{p.method}</span>{' '}
                      <span style={{ color: C.dim }}>{p.path}</span>
                    </div>
                    <div style={{ color: C.faint, fontSize: 10, marginTop: 2 }}>
                      {formatRelative(p.attempted_at)}
                    </div>
                    <div className="flex items-center gap-2" style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        onClick={() => onRetry(p.id)}
                        disabled={isRetrying}
                        style={{
                          background: C.signal,
                          color: C.bg,
                          border: 0,
                          borderRadius: 0,
                          padding: '8px 12px',
                          minHeight: 36,
                          fontFamily: FONT_MONO,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.2em',
                          textTransform: 'uppercase',
                          cursor: isRetrying ? 'not-allowed' : 'pointer',
                          opacity: isRetrying ? 0.6 : 1,
                        }}
                      >
                        {isRetrying ? 'Retrying…' : 'Retry'}
                      </button>
                      <button
                        type="button"
                        onClick={() => dismissPendingWrite(p.id)}
                        disabled={isRetrying}
                        style={{
                          background: 'transparent',
                          color: C.faint,
                          border: `1px solid ${C.line}`,
                          borderRadius: 0,
                          padding: '8px 12px',
                          minHeight: 36,
                          fontFamily: FONT_MONO,
                          fontSize: 10,
                          letterSpacing: '0.2em',
                          textTransform: 'uppercase',
                          cursor: isRetrying ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
